import { openInNewTab } from '@/components/Functional/utils/openInNewTab'
import type { UserRewardStatusDetailData } from '@/schemas/v2'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { getRewardActionButtonLabel } from './rewardActionButtonLabel'

type Props = {
  /** 処理中（acquiring / exchanging）かどうか */
  isLoading?: boolean
  /** データ読み込み中（userRewardStatus が未確定）かどうか */
  isDataLoading?: boolean
  userRewardStatus: UserRewardStatusDetailData | null
  isAuthenticated: boolean
  onAcquire: () => void
  /**
   * 現在のセッションでギフトコードがメモリ（state.context.transaction）に存在するかどうか。
   * ACHIEVED + GIFT_CODE 時に再表示ボタンを有効化するために使用。
   * ページリロード後は false になる。
   */
  hasGiftCodeInMemory?: boolean
}

/**
 * 特典詳細ページのアクションボタン。
 * ユーザーの認証状態・特典獲得状況に応じてボタンラベルと活性状態を変える。
 * isDataLoading=true の間はスケルトン表示を返す。
 */
export const RewardActionButton = memo(
  ({
    isLoading = false,
    isDataLoading = false,
    userRewardStatus,
    isAuthenticated,
    onAcquire,
    hasGiftCodeInMemory = false,
  }: Props) => {
    const { t } = useTranslation()

    if (isDataLoading) {
      return <div className="h-[36px] w-full animate-pulse rounded-[8px] bg-gray-200" aria-hidden="true" />
    }

    if (!isAuthenticated) {
      return (
        <button
          type="button"
          onClick={onAcquire}
          className="flex h-[36px] w-full items-center justify-center rounded-[8px] bg-main text-[13px] font-bold text-sub"
        >
          {t('reward_detail.login_required', 'ログインして獲得する')}
        </button>
      )
    }

    const isRepeatable = userRewardStatus?.isRepeatable ?? false
    const prizes = userRewardStatus?.prizes ?? []
    const status = userRewardStatus?.status
    const selectionType = userRewardStatus?.prizeSelectionType

    // EXCHANGED + DLC + contentFile + !isRepeatable → active（タブ直接オープン）
    const isDlcExchanged =
      status === 'EXCHANGED' && !isRepeatable && prizes.some((p) => p.prize?.type === 'DLC' && !!p.prize?.contentFile)

    // EXCHANGED + GIFT_CODE: APIレスポンスのコードがある → 再表示可能
    const isGiftCodeExchanged =
      status === 'EXCHANGED' && prizes.some((p) => p.prize?.type === 'GIFT_CODE' && p.prize?.code != null)

    // ACHIEVED + GIFT_CODE + セッションメモリあり + !isRepeatable → active「賞品を受け取る」
    const isGiftCodeAchievedActive =
      hasGiftCodeInMemory && status === 'ACHIEVED' && !isRepeatable && prizes.some((p) => p.prize?.type === 'GIFT_CODE')

    // prizes が空 or 全エントリがハズレ = ハズレ確定
    const isAchievedLotteryLose =
      status === 'ACHIEVED' &&
      selectionType === 'LOTTERY' &&
      (prizes.length === 0 || prizes.every((p) => p.isLotteryLost))

    // ACHIEVED + APPLICATION_FORM: 応募フロー再開のため enabled にする
    const isApplicationFormAchieved =
      status === 'ACHIEVED' && prizes.some((p) => !p.isLotteryLost && p.prize?.fulfillmentType === 'APPLICATION_FORM')

    // ACHIEVED + COUPON + isRepeatable=true → 無効化「獲得済み」
    const isAchievedCouponRepeatable =
      status === 'ACHIEVED' && isRepeatable && prizes.some((p) => !p.isLotteryLost && p.prize?.type === 'COUPON')

    // ACHIEVED + COUPON + isRepeatable=false → 活性化「使用する」（クーポン使用フロー継続のため）
    const isAchievedCouponNotRepeatable =
      status === 'ACHIEVED' && !isRepeatable && prizes.some((p) => !p.isLotteryLost && p.prize?.type === 'COUPON')

    // ACHIEVED + GIFT_CODE + isRepeatable=true → 無効化
    const isAchievedGiftCodeRepeatable =
      status === 'ACHIEVED' && isRepeatable && prizes.some((p) => p.prize?.type === 'GIFT_CODE')

    // ACHIEVED + GIFT_CODE + !isRepeatable + !hasGiftCodeInMemory (リロード後) → 無効化「獲得済み」
    // ステートマシンが isAchievedGiftCodeWithContext → idle で no-op のため、UI と一致させる
    const isAchievedGiftCodeWithoutMemory =
      !hasGiftCodeInMemory &&
      status === 'ACHIEVED' &&
      !isRepeatable &&
      prizes.some((p) => p.prize?.type === 'GIFT_CODE')

    // ACHIEVED + GIFTEE + isRepeatable=true → 無効化「獲得済み」
    const isAchievedGifteeRepeatable =
      status === 'ACHIEVED' && isRepeatable && prizes.some((p) => !p.isLotteryLost && p.prize?.type === 'GIFTEE')

    // ACHIEVED + DLC + isRepeatable=true → 無効化「獲得済み」
    const isAchievedDlcRepeatable =
      status === 'ACHIEVED' && isRepeatable && prizes.some((p) => !p.isLotteryLost && p.prize?.type === 'DLC')

    // ACHIEVED + AR_PHOTO
    const isAchievedArPhoto =
      status === 'ACHIEVED' && prizes.some((p) => !p.isLotteryLost && p.prize?.type === 'AR_PHOTO')

    const isAchievedArPhotoRepeatable = isAchievedArPhoto && isRepeatable

    // EXCHANGED + AR_PHOTO + !isRepeatable → active（ARカメラ起動）
    const isArPhotoExchanged =
      status === 'EXCHANGED' && !isRepeatable && prizes.some((p) => p.prize?.type === 'AR_PHOTO')

    // ACHIEVED + ITEM（APPLICATION_FORM 以外）
    const isAchievedItem =
      status === 'ACHIEVED' &&
      prizes.some(
        (p) => !p.isLotteryLost && p.prize?.type === 'ITEM' && p.prize?.fulfillmentType !== 'APPLICATION_FORM',
      )

    const isAchievedItemRepeatable = isAchievedItem && isRepeatable

    // UNAPPLIED + isRepeatable=true → disabled（応募は獲得賞品一覧ダイアログから行う）
    const isUnappliedRepeatable = status === 'UNAPPLIED' && isRepeatable

    // EXCHANGED + GIFTEE + LOTTERY → active（再表示可能）
    const isGifteeExchangedLotteryActive =
      status === 'EXCHANGED' && selectionType === 'LOTTERY' && prizes.some((p) => p.prize?.type === 'GIFTEE')

    const isDisabled =
      status === 'UNOBTAINED' ||
      (status === 'EXCHANGED' &&
        !isDlcExchanged &&
        !isGiftCodeExchanged &&
        !isGifteeExchangedLotteryActive &&
        !isArPhotoExchanged) ||
      status === 'USED' ||
      status === 'APPLIED' ||
      status === 'LOST' ||
      status === 'ENDED' ||
      status === 'SOLD_OUT' ||
      isAchievedLotteryLose ||
      // isApplicationFormAchieved は除外: ACHIEVED + APPLICATION_FORM は enabled にして応募フローを再開できるようにする
      isAchievedCouponRepeatable ||
      isAchievedGiftCodeRepeatable ||
      isAchievedGiftCodeWithoutMemory ||
      isAchievedGifteeRepeatable ||
      isAchievedDlcRepeatable ||
      isAchievedArPhotoRepeatable ||
      isAchievedItemRepeatable ||
      isUnappliedRepeatable ||
      isLoading

    const { label } = getRewardActionButtonLabel({
      t,
      isDlcExchanged,
      isGiftCodeExchanged,
      isGiftCodeAchievedActive,
      status,
      prizes,
      selectionType,
      isRepeatable,
      isAchievedLotteryLose,
      isApplicationFormAchieved,
      isAchievedCouponRepeatable,
      isAchievedCouponNotRepeatable,
      isAchievedArPhotoRepeatable,
      isAchievedArPhoto,
      isAchievedGiftCodeRepeatable,
      isAchievedGiftCodeWithoutMemory,
      isAchievedGifteeRepeatable,
      isAchievedDlcRepeatable,
      isAchievedItemRepeatable,
      isAchievedItem,
    })

    const dlcContentFile =
      (userRewardStatus?.prizes ?? []).find((p) => p.prize?.type === 'DLC')?.prize?.contentFile ?? null

    const handleClick = () => {
      if (isDlcExchanged && dlcContentFile) {
        openInNewTab(dlcContentFile)
        return
      }
      onAcquire()
    }

    return (
      <button
        type="button"
        disabled={isDisabled}
        onClick={handleClick}
        className={
          status === 'ENDED' || status === 'SOLD_OUT'
            ? 'flex h-[36px] w-full items-center justify-center rounded-[8px] bg-gray-300 text-[13px] font-bold text-gray-500'
            : 'flex h-[36px] w-full items-center justify-center rounded-[8px] bg-main text-[13px] font-bold text-sub disabled:bg-main-50 disabled:text-sub'
        }
      >
        {isLoading ? (
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-sub border-t-transparent" />
        ) : (
          label
        )}
      </button>
    )
  },
)
